import "./dayjs-config";
import "./firebase";

import { ModalsProvider } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import Backdrop from "components/Backdrop";
import OnlineStatus from "components/OnlineStatus";
import RequireAuth from "components/RequireAuth";
import { getToken } from "firebase/messaging";
import isElectron from "helpers/isElectron";
import { observer } from "mobx-react-lite";
import React, { lazy, Suspense, useContext, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  createBrowserRouter,
  createHashRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import ErrorFallbackPage from "scenes/ErrorFallbackPage";
import { registerServiceWorker } from "serviceWorker";
import { RootStoreContext } from "stores/RootStore";

import { messaging } from "./firebase";
import { useTokenRefresh } from "./hooks/useTokenRefresh";

const PurchaseInvoicePaymentDetailsPage = lazy(
  () => import("./scenes/PurchaseInvoicePaymentDetailsPage")
);
const CustomersOverviewPage = lazy(
  () => import("./scenes/CustomersOverviewPage")
);
const HomePage = lazy(() => import("./scenes/HomePage"));
const RegistrationPage = lazy(() => import("./scenes/RegistrationPage"));
const DashboardPage = lazy(() => import("./scenes/DashboardPage"));
const DeliveryTrackingPage = lazy(
  () => import("./scenes/DeliveryTrackingPage")
);
const ErrorPage = lazy(() => import("./scenes/ErrorPage"));
const JournalsPage = lazy(() => import("./scenes/JournalsPage"));
const AddJournalsPage = lazy(() => import("./scenes/AddJournalsPage"));
const LedgersPage = lazy(() => import("./scenes/LedgersPage"));
const LedgerDetailsPage = lazy(
  () => import("./scenes/LedgersPage/LedgerDetailsPage")
);
const LedgerMonthlySummaryPage = lazy(
  () => import("./scenes/LedgersPage/LedgerMonthlySummaryPage")
);

const MyProfilePage = lazy(() => import("./scenes/MyProfilePage"));
const OnlineOrderDetailsPage = lazy(
  () => import("./scenes/OnlineOrderDetailsPage")
);
const OnlinePickupsPage = lazy(() => import("./scenes/OnlinePickupsPage"));
const OfflinePickupsPage = lazy(() => import("./scenes/OfflinePickupsPage"));
const OnlineOrdersPage = lazy(() => import("./scenes/OnlineOrdersPage"));
const AddPickupDetailsPage = lazy(
  () => import("./scenes/AddPickupDetailsPage")
);
const UploadInventoryPage = lazy(() => import("./scenes/UploadInventoryPage"));
const UploadPurchaseInvoiceCSVPage = lazy(
  () => import("./scenes/UploadPurchaseInvoiceCSVPage")
);
const ViewProductsPage = lazy(() => import("./scenes/ViewProductsPage"));
const MedicoInvoicesPage = lazy(() => import("./scenes/MedicoInvoicesPage"));
const NotificationsPage = lazy(() => import("./scenes/NotificationsPage"));
const ConnectedUsersPage = lazy(() => import("./scenes/ConnectedUsersPage"));
const UpiAccountDetailsPage = lazy(
  () => import("./scenes/UpiAccountDetailsPage")
);
const CreateUpiPaymentPage = lazy(
  () => import("./scenes/CreateUpiPaymentPage")
);
const ReceivePaymentsPage = lazy(() => import("./scenes/ReceivePaymentsPage"));
const MakePaymentsPage = lazy(() => import("./scenes/MakePaymentsPage"));
const UpiAccountsPage = lazy(() => import("./scenes/UpiAccountsPage"));
const AddUpiAccountPage = lazy(() => import("./scenes/AddUpiAccountPage"));
const PaymentsPage = lazy(() => import("./scenes/PaymentsPage"));
const CartPage = lazy(() => import("./scenes/CartPage"));
const WarehousesPage = lazy(
  () => import("./scenes/WarehousesPage/WarehousesPage")
);
const CustomerOrderDetailsPage = lazy(
  () => import("./scenes/CustomersOverviewPage/CustomerOrderDetailsPage")
);
const PrivacyPolicyPage = lazy(() => import("./scenes/PrivacyPolicyPage"));
const TermsAndConditionsPage = lazy(
  () => import("./scenes/TermsAndConditionsPage")
);
const AccountsPrefixPage = lazy(
  () => import("./scenes/AccountsPrefixPage/AccountsPrefixPage")
);
const BankAccountsPage = lazy(
  () => import("./scenes/BankAccountsPage/BankAccountsPage")
);
const GlobalSearchResultsPage = lazy(
  () => import("./scenes/GlobalSearchResultsPage")
);
const ProfitLossPage = lazy(() => import("./scenes/ProfitLossPage"));
const PurchaseInvoicesPage = lazy(
  () => import("./scenes/PurchaseInvoicesPage")
);
const AddPurchaseInvoiceOrderPage = lazy(
  () => import("./scenes/AddPurchaseInvoiceOrderPage")
);
const PurchaseInvoiceDetailsPage = lazy(
  () => import("./scenes/PurchaseInvoiceDetailsPage")
);
const PurchaseReportsPage = lazy(() => import("./scenes/PurchaseReportsPage"));
const PurchaseReportDetailsPage = lazy(
  () => import("./scenes/PurchaseReportDetailsPage")
);
const PurchaseReturnsPage = lazy(() => import("./scenes/PurchaseReturnsPage"));
const AddInstoreSalesPage = lazy(() => import("./scenes/AddInstoreSalesPage"));
const InstoreSalesPage = lazy(() => import("./scenes/InstoreSalesPage"));
const InstoreSalesDetailsPage = lazy(
  () => import("./scenes/InstoreSalesDetailsPage")
);
const InstoreSaleEditPage = lazy(() => import("./scenes/InstoreSaleEditPage"));
const SalesPreviewPage = lazy(() => import("./scenes/SalesPreviewPage"));
const OnlineSalesPage = lazy(() => import("./scenes/SalesPage"));
const SalesInvoicePage = lazy(() => import("./scenes/SalesInvoicePage"));
const SalesReportPage = lazy(() => import("./scenes/SalesReportsPage"));
const OrdersOverviewSimplePage = lazy(
  () => import("./scenes/OrdersOverviewSimplePage/OrdersOverviewSimplePage")
);

const ProductsOverviewSimplePage = lazy(
  () => import("./scenes/ProductsOverviewSimplePage/ProductsOverviewSimplePage")
);

const ProductEditPage = lazy(() => import("./scenes/ProductEditPage"));
const OrderDetailsPage = lazy(
  () => import("./scenes/OrdersOverviewSimplePage/OrderDetailsPage")
);
const SalesReportDetailsPage = lazy(
  () => import("./scenes/SalesReportDetailsPage")
);
const ReturnSalesPage = lazy(
  () => import("./scenes/SalesPage/ReturnSalesPage")
);
const InvoiceDetailsPage = lazy(() =>
  import("./scenes/SalesPage/components/Returns").then((module) => ({
    default: module.InvoiceDetailsPage,
  }))
);

// Individual Report Pages
const PartyWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/PurchaseReports/PartyWiseReportPage")
);
const ItemWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/PurchaseReports/ItemWiseReportPage")
);
const BatchWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/PurchaseReports/BatchWiseReportPage")
);
const MfrWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/PurchaseReports/MfrWiseReportPage")
);
const PurchaseRegistryReportPage = lazy(
  () =>
    import("./scenes/ReportsPage/PurchaseReports/PurchaseRegistryReportPage")
);
const DayWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/SalesReports/DayWiseReportPage")
);
const CustomersReportPage = lazy(
  () => import("./scenes/ReportsPage/SalesReports/CustomersReportPage")
);
const SalesItemWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/SalesReports/ItemWiseReportPage")
);
const SalesBatchWiseReportPage = lazy(
  () => import("./scenes/ReportsPage/SalesReports/BatchWiseReportPage")
);
const SalesRegistryReportPage = lazy(
  () => import("./scenes/ReportsPage/SalesReports/SalesRegistryReportPage")
);
const DayBookReportPage = lazy(
  () => import("./scenes/ReportsPage/JournalReports/DayBookReportPage")
);

const CashBookReportPage = lazy(
  () => import("./scenes/ReportsPage/JournalReports/CashBookReportPage")
);
const CashBookDetailsPage = lazy(
  () => import("./scenes/ReportsPage/JournalReports/CashBookDetailsPage")
);

const TrailBalanceReportPage = lazy(
  () => import("./scenes/ReportsPage/JournalReports/TrailBalanceReportPage")
);

const DebitorsReportPage = lazy(
  () => import("./scenes/ReportsPage/JournalReports/DebitorsReportPage")
);

const CreditorsReportPage = lazy(
  () => import("./scenes/ReportsPage/JournalReports/CreditorsReportPage")
);

const HeadsPage = lazy(() => import("./scenes/HeadsPage"));
const AddHeadsPage = lazy(() => import("./scenes/AddHeadsPage"));

const StoresDashboardPage = lazy(() => import("./scenes/StoresPage"));
const StoreDetailsPage = lazy(() => import("./scenes/StoreDetailsPage"));

const StocksPage = lazy(() => import("./scenes/StocksPage"));
const AddStocksPage = lazy(() => import("./scenes/AddStocksPage"));
const StockItemDetailsPage = lazy(
  () => import("./scenes/StockItemDetailsPage")
);
const StockReportPage = lazy(() => import("./scenes/StocksReportPage"));
const StockReportDetailsPage = lazy(
  () => import("./scenes/StockReportDetailsPage")
);
const ExpiryStockListPage = lazy(() => import("./scenes/ExpiryStockPage"));
const ExpiryStockDetailsPage = lazy(
  () => import("./scenes/ExpiryStockDetailsPage")
);
const StockBatchExpiryPage = lazy(
  () => import("./scenes/StockBatchExpiryPage")
);
const StockSummaryPage = lazy(() => import("./scenes/StockSummaryPage"));
const StockFastSlowMovingPage = lazy(
  () => import("./scenes/StockFastSlowMovingPage")
);
const OutwardStockRegistryDetailsPage = lazy(
  () => import("./scenes/OutwardStockRegistryDetailsPage")
);
const StockProductsListPage = lazy(
  () => import("./scenes/StockProductsListPage")
);
const OrdersOverviewPage = lazy(() => import("./scenes/OrdersOverviewPage"));
const OrdersOverviewDetailsPage = lazy(
  () => import("./scenes/OrdersOverviewDetailsPage")
);
const PurchaseReturnsDetailsPage = lazy(
  () => import("./scenes/PurchaseReturnsDetailsPage")
);
const PurchaseReturnPaymentDetailsPage = lazy(
  () => import("./scenes/PurchaseReturnPaymentDetailsPage")
);
const ManualSelectionPage = lazy(() => import("./scenes/ManualSelectionPage"));
const SalesReturnPaymentDetailsPage = lazy(
  () => import("./scenes/SalesReturnPaymentDetailsPage")
);
const OutwardStockRegistryPage = lazy(
  () => import("./scenes/OutwardStockRegistryPage")
);
const AddOutwardStockRegistryPage = lazy(
  () => import("./scenes/AddOutwardStockRegistryPage")
);
const Memo2PaymentPage = lazy(() => import("./scenes/Memo2PaymentPage"));
const B2BCustomersPage = lazy(() => import("./scenes/B2BCustomersPage"));
const B2BCustomerDetailsPage = lazy(
  () => import("./scenes/B2BCustomersPage/B2BCustomerDetailsPage")
);

const AddSaleQuotationPage = lazy(
  () => import("./scenes/AddSaleQuotationPage")
);
const AddSalesReturnPage = lazy(() => import("./scenes/AddSalesReturnPage"));
const SalesReturnsPage = lazy(() => import("./scenes/SalesReturnsPage"));
const PurchaseOrdersPage = lazy(() => import("./scenes/PurchaseOrdersPage"));
const EmployeesPage = lazy(
  () => import("./scenes/EmployeesPage/EmployeesPage")
);
const EmployeeDetailsPage = lazy(
  () => import("./scenes/EmployeeDetailsPage/EmployeeDetailsPage")
);

const AddPurchaseOrderPage = lazy(
  () => import("./scenes/AddPurchaseOrderPage")
);
const PreviewPurchaseOrderPage = lazy(
  () => import("./scenes/PreviewPurchaseOrderPage")
);

// GST Pages
const InwardSuppliersPage = lazy(
  () => import("./scenes/GSTPage/InwardSuppliersPage")
);
const OutwardSuppliersPage = lazy(
  () => import("./scenes/GSTPage/OutwardSuppliersPage")
);
const CompanyGSTPage = lazy(() => import("./scenes/GSTPage/CompanyGSTPage"));
const PurchaseOrderDetailsPage = lazy(
  () => import("./scenes/PurchaseOrderDetailsPage")
);
const InstoreSalesPaymentPage = lazy(
  () => import("./scenes/InstoreSalesPaymentPage")
);
const OffersPage = lazy(() => import("./scenes/OffersPage"));
const AISupportPage = lazy(() => import("./scenes/AISupportPage"));
const CollectionsPage = lazy(() => import("./scenes/CollectionsPage"));

function requestPermission() {
  Notification.requestPermission().then((permission) => {
    if (permission === "granted") {
      notifications.show({
        title: "Permission Granted",
        message: "You can now receive notifications",
        color: "green",
      });
    } else {
      notifications.show({
        title: "Permission Denied",
        message: "You will not receive notifications",
        color: "red",
      });
    }
  });
}

const App: React.FC = observer(() => {
  const { authStore, isHydrated } = useContext(RootStoreContext);

  const [hydrated, setHydrated] = useState(isHydrated);

  // Proactive token refresh - refreshes token 1 minute before expiry
  useTokenRefresh();

  if (authStore.isAuthorized) {
    registerServiceWorker();
  }

  useEffect(() => {
    if (!isHydrated) {
      setHydrated(true);
    }
  }, [isHydrated]);

  useEffect(() => {
    if (hydrated && authStore.isAuthorized) {
      getToken(messaging, { vapidKey: process.env.REACT_APP_PUBLIC_VAPID_KEY })
        .then(async (currentToken) => {
          if (!authStore?.isAuthorized) {
            return;
          }

          if (currentToken) {
            authStore
              .saveFcmToken(
                currentToken,
                "WEB",
                authStore.currentOsType,
                authStore.currentDeviceId,
                authStore.currentDeviceType
              )
              .then((r) => {
                if (r?.type === "error") {
                  notifications.show({
                    title: r.error.title,
                    message: r.error.body,
                    color: "red",
                  });
                }
              });
          } else {
            requestPermission();

            notifications.show({
              title: "Permission Required",
              message: "You need to grant permission to receive notifications",
              color: "red",
            });
          }
        })
        .catch((e) => {
          // notifications.show({
          //   title: "Warning",
          //   message: "You need to grant permission to receive notifications",
          //   color: "orange",
          // });

          // eslint-disable-next-line no-console
          console.log("An error occurred while retrieving token. ", e);
        });
    }
  }, [authStore.isAuthorized, hydrated]);

  // Prevent mouse wheel from changing values on number inputs
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const activeEl = document.activeElement as HTMLElement | null;
      if (
        activeEl &&
        activeEl.tagName === "INPUT" &&
        (activeEl as HTMLInputElement).type === "number"
      ) {
        event.preventDefault();
      }
    };

    const handleArrowKeys = (event: KeyboardEvent) => {
      const activeEl = document.activeElement as HTMLElement | null;
      if (
        activeEl &&
        activeEl.tagName === "INPUT" &&
        (activeEl as HTMLInputElement).type === "number" &&
        (event.key === "ArrowUp" || event.key === "ArrowDown")
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("keydown", handleArrowKeys, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel as EventListener);
      window.removeEventListener("keydown", handleArrowKeys as EventListener);
    };
  }, []);

  const stockistsConnectedUsers = [
    {
      path: "customers",
      children: [
        {
          index: true,
          element: (
            <RequireAuth>
              <ConnectedUsersPage connectedUserType="STOCKIST" />
            </RequireAuth>
          ),
        },
        {
          path: "stockists",
          element: (
            <RequireAuth>
              <ConnectedUsersPage connectedUserType="STOCKIST" />
            </RequireAuth>
          ),
        },
        {
          path: "hospitals",
          element: (
            <RequireAuth>
              <ConnectedUsersPage connectedUserType="HOSPITAL" />
            </RequireAuth>
          ),
        },
        {
          path: "retailers",
          element: (
            <RequireAuth>
              <ConnectedUsersPage connectedUserType="RETAILER" />
            </RequireAuth>
          ),
        },
      ],
    },
  ];

  const routerInitial = [
    {
      path: "/",
      element: <Navigate to="/dashboard" replace />,
    },
    {
      path: "dashboard",
      element: (
        <RequireAuth>
          <DashboardPage />
        </RequireAuth>
      ),
    },
    {
      path: "delivery-tracking",
      element: (
        <RequireAuth>
          <DeliveryTrackingPage />
        </RequireAuth>
      ),
    },
    {
      path: "/orders-overview-simple",
      element: (
        <RequireAuth>
          <OrdersOverviewSimplePage />
        </RequireAuth>
      ),
    },
    {
      path: "privacy-policy",
      element: <PrivacyPolicyPage />,
    },
    {
      path: "terms-and-conditions",
      element: <TermsAndConditionsPage />,
    },
    {
      path: "/orders-overview-simple/:id",
      element: (
        <RequireAuth>
          <OrderDetailsPage />
        </RequireAuth>
      ),
    },
    {
      path: "/products-overview-simple",
      element: (
        <RequireAuth>
          <ProductsOverviewSimplePage />
        </RequireAuth>
      ),
    },
    {
      path: "/products/edit/:id",
      element: (
        <RequireAuth>
          <ProductEditPage />
        </RequireAuth>
      ),
    },
    {
      path: "login",
      element: <HomePage />,
    },
    {
      path: "registration",
      element: <RegistrationPage />,
    },
    {
      path: "profile",
      element: (
        <RequireAuth>
          <MyProfilePage />
        </RequireAuth>
      ),
    },
    ...stockistsConnectedUsers,
    {
      path: "billing",
      children: [
        {
          path: "invoices",
          element: (
            <RequireAuth>
              <MedicoInvoicesPage />
            </RequireAuth>
          ),
        },
        {
          path: "upi-account",
          element: (
            <RequireAuth>
              <UpiAccountDetailsPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "payments",
      children: [
        {
          path: "list",
          element: (
            <RequireAuth>
              <PaymentsPage />
            </RequireAuth>
          ),
        },
        {
          path: "collections",
          element: (
            <RequireAuth>
              <CollectionsPage />
            </RequireAuth>
          ),
        },
        {
          path: "upi",
          children: [
            {
              path: "create",
              element: (
                <RequireAuth>
                  <CreateUpiPaymentPage />
                </RequireAuth>
              ),
            },
            {
              path: "receive",
              element: (
                <RequireAuth>
                  <ReceivePaymentsPage />
                </RequireAuth>
              ),
            },
            {
              path: "make",
              element: (
                <RequireAuth>
                  <MakePaymentsPage />
                </RequireAuth>
              ),
            },
            {
              path: "accounts",
              children: [
                {
                  index: true,
                  element: (
                    <RequireAuth>
                      <UpiAccountsPage />
                    </RequireAuth>
                  ),
                },
                {
                  path: "add",
                  element: (
                    <RequireAuth>
                      <AddUpiAccountPage />
                    </RequireAuth>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "inventory",
      children: [
        {
          path: "upload",
          element: (
            <RequireAuth>
              <UploadInventoryPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "cart",
      element: (
        <RequireAuth>
          <CartPage />
        </RequireAuth>
      ),
    },
    {
      path: "search",
      element: (
        <RequireAuth>
          <GlobalSearchResultsPage />
        </RequireAuth>
      ),
    },
    {
      path: "ai-support",
      element: (
        <RequireAuth>
          <AISupportPage />
        </RequireAuth>
      ),
    },
    {
      path: "/customers-overview",
      element: (
        <RequireAuth>
          <CustomersOverviewPage />
        </RequireAuth>
      ),
    },
    {
      path: "/customers-orders/:id",
      element: (
        <RequireAuth>
          <CustomerOrderDetailsPage />
        </RequireAuth>
      ),
    },
    {
      path: "viewproducts/:id",
      element: (
        <RequireAuth>
          <ViewProductsPage />
        </RequireAuth>
      ),
    },
    {
      path: "appproducts",
      element: (
        <RequireAuth>
          <React.Suspense fallback={<Backdrop />}>
            {React.createElement(
              lazy(() => import("./scenes/AppProductsPage"))
            )}
          </React.Suspense>
        </RequireAuth>
      ),
    },

    {
      path: "notifications",
      element: (
        <RequireAuth>
          <NotificationsPage />
        </RequireAuth>
      ),
    },
    {
      path: "purchases",
      children: [
        {
          path: "invoices",
          element: (
            <RequireAuth>
              <PurchaseInvoicesPage />
            </RequireAuth>
          ),
        },
        {
          path: "invoice/:id",
          element: (
            <RequireAuth>
              <PurchaseInvoiceDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "invoice/:id/payment",
          element: (
            <RequireAuth>
              <PurchaseInvoicePaymentDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "invoices/add-purchase-order",
          element: (
            <RequireAuth>
              <AddPurchaseInvoiceOrderPage />
            </RequireAuth>
          ),
        },
        {
          path: "invoices/upload-csv",
          element: (
            <RequireAuth>
              <UploadPurchaseInvoiceCSVPage />
            </RequireAuth>
          ),
        },
        {
          path: "csv/upload/:uploadId",
          element: (
            <RequireAuth>
              <UploadPurchaseInvoiceCSVPage />
            </RequireAuth>
          ),
        },
        {
          path: "reports",
          element: (
            <RequireAuth>
              <PurchaseReportsPage />
            </RequireAuth>
          ),
        },
        {
          path: "report/:id",
          element: (
            <RequireAuth>
              <PurchaseReportDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns",
          element: (
            <RequireAuth>
              <PurchaseReturnsPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns/manual-selection",
          element: (
            <RequireAuth>
              <ManualSelectionPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns/:id",
          element: (
            <RequireAuth>
              <PurchaseReturnsDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "return/:id/payment",
          element: (
            <RequireAuth>
              <PurchaseReturnPaymentDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "orders",
          element: (
            <RequireAuth>
              <PurchaseOrdersPage />
            </RequireAuth>
          ),
        },
        {
          path: "orders/:id",
          element: (
            <RequireAuth>
              <AddPurchaseOrderPage />
            </RequireAuth>
          ),
        },
        {
          path: "orders/:id/view",
          element: (
            <RequireAuth>
              <PurchaseOrderDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "orders/preview",
          element: (
            <RequireAuth>
              <PreviewPurchaseOrderPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "sales",
      children: [
        {
          path: "add-instore/:id",
          element: (
            <RequireAuth>
              <AddInstoreSalesPage />
            </RequireAuth>
          ),
        },
        {
          path: "instore",
          element: (
            <RequireAuth>
              <InstoreSalesPage />
            </RequireAuth>
          ),
        },
        {
          path: "instore/:id",
          element: (
            <RequireAuth>
              <InstoreSalesDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "instore/:id/payment",
          element: (
            <RequireAuth>
              <InstoreSalesPaymentPage />
            </RequireAuth>
          ),
        },
        {
          path: "instore-add/:id",
          element: (
            <RequireAuth>
              <InstoreSaleEditPage />
            </RequireAuth>
          ),
        },
        {
          path: "add-instore/preview/:id",
          element: (
            <RequireAuth>
              <SalesPreviewPage />
            </RequireAuth>
          ),
        },
        {
          path: "online",
          element: (
            <RequireAuth>
              <OnlineSalesPage />
            </RequireAuth>
          ),
        },
        {
          path: "reports",
          element: (
            <RequireAuth>
              <SalesReportPage />
            </RequireAuth>
          ),
        },
        {
          path: "report/details",
          element: (
            <RequireAuth>
              <SalesReportDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "report/details/invoice",
          element: (
            <RequireAuth>
              <SalesInvoicePage />
            </RequireAuth>
          ),
        },
        {
          path: "returns",
          element: (
            <RequireAuth>
              <ReturnSalesPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns/invoice-details/:returnId",
          element: (
            <RequireAuth>
              <InvoiceDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns/add/:id",
          element: (
            <RequireAuth>
              <AddSalesReturnPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns/:id",
          element: (
            <RequireAuth>
              <SalesReturnsPage />
            </RequireAuth>
          ),
        },
        {
          path: "returns/:returnId/payment",
          element: (
            <RequireAuth>
              <SalesReturnPaymentDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "quotation/:id",
          element: (
            <RequireAuth>
              <AddSaleQuotationPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "stores",
      children: [
        {
          path: "dashboard",
          element: (
            <RequireAuth>
              <StoresDashboardPage />
            </RequireAuth>
          ),
        },
        {
          path: "details",
          element: (
            <RequireAuth>
              <StoreDetailsPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "warehouses",
      element: (
        <RequireAuth>
          <WarehousesPage />
        </RequireAuth>
      ),
    },
    {
      path: "accounts-prefix",
      element: (
        <RequireAuth>
          <AccountsPrefixPage />
        </RequireAuth>
      ),
    },
    {
      path: "bank-accounts",
      element: (
        <RequireAuth>
          <BankAccountsPage />
        </RequireAuth>
      ),
    },
    {
      path: "heads",
      children: [
        {
          path: "dashboard",
          element: (
            <RequireAuth>
              <HeadsPage />
            </RequireAuth>
          ),
        },
        {
          path: "add",
          element: (
            <RequireAuth>
              <AddHeadsPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "stocks",
      children: [
        {
          path: "list",
          element: (
            <RequireAuth>
              <StocksPage />
            </RequireAuth>
          ),
        },
        {
          path: "add",
          element: (
            <RequireAuth>
              <AddStocksPage />
            </RequireAuth>
          ),
        },
        {
          path: ":id",
          element: (
            <RequireAuth>
              <StockItemDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "reports",
          element: (
            <RequireAuth>
              <StockReportPage />
            </RequireAuth>
          ),
        },
        {
          path: "reports/details",
          element: (
            <RequireAuth>
              <StockReportDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "expiry-list",
          element: (
            <RequireAuth>
              <ExpiryStockListPage />
            </RequireAuth>
          ),
        },
        {
          path: "expiry/details",
          element: (
            <RequireAuth>
              <ExpiryStockDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: "batch-expiry",
          element: (
            <RequireAuth>
              <StockBatchExpiryPage />
            </RequireAuth>
          ),
        },
        {
          path: "summary",
          element: (
            <RequireAuth>
              <StockSummaryPage />
            </RequireAuth>
          ),
        },
        {
          path: "fast-slow-moving",
          element: (
            <RequireAuth>
              <StockFastSlowMovingPage />
            </RequireAuth>
          ),
        },
        {
          path: "products-list",
          element: (
            <RequireAuth>
              <StockProductsListPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "orders",
      children: [
        {
          path: "offline-pickups",
          children: [
            {
              index: true,
              element: (
                <RequireAuth>
                  <OfflinePickupsPage />
                </RequireAuth>
              ),
            },
            {
              path: "add",
              element: (
                <RequireAuth>
                  <AddPickupDetailsPage />
                </RequireAuth>
              ),
            },
          ],
        },
        {
          path: "online-pickups",
          element: (
            <RequireAuth>
              <OnlinePickupsPage />
            </RequireAuth>
          ),
        },
        {
          path: "online-orders",
          children: [
            {
              index: true,
              element: (
                <RequireAuth>
                  <OnlineOrdersPage />
                </RequireAuth>
              ),
            },
            {
              path: ":orderId",
              element: (
                <RequireAuth>
                  <OnlineOrderDetailsPage />
                </RequireAuth>
              ),
            },
          ],
        },
        {
          path: "orders-overview",
          children: [
            {
              index: true,
              element: (
                <RequireAuth>
                  <OrdersOverviewPage />
                </RequireAuth>
              ),
            },
            {
              path: ":barcodeId",
              element: (
                <RequireAuth>
                  <OrdersOverviewDetailsPage />
                </RequireAuth>
              ),
            },
          ],
        },
        {
          path: "b2b-customers",
          children: [
            {
              index: true,
              element: (
                <RequireAuth>
                  <B2BCustomersPage />
                </RequireAuth>
              ),
            },
            {
              path: ":id",
              element: (
                <RequireAuth>
                  <B2BCustomerDetailsPage />
                </RequireAuth>
              ),
            },
          ],
        },
        {
          path: "outward-stock-registry",
          children: [
            {
              index: true,
              element: (
                <RequireAuth>
                  <OutwardStockRegistryPage />
                </RequireAuth>
              ),
            },
            {
              path: "add",
              element: (
                <RequireAuth>
                  <AddOutwardStockRegistryPage />
                </RequireAuth>
              ),
            },
            {
              path: ":id",
              element: (
                <RequireAuth>
                  <OutwardStockRegistryDetailsPage />
                </RequireAuth>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "ledgers",
      children: [
        {
          path: "",
          element: (
            <RequireAuth>
              <LedgersPage />
            </RequireAuth>
          ),
        },
        {
          path: ":ledgerId/summary",
          element: (
            <RequireAuth>
              <LedgerMonthlySummaryPage />
            </RequireAuth>
          ),
        },
        {
          path: ":ledgerId/details",
          element: (
            <RequireAuth>
              <LedgerDetailsPage />
            </RequireAuth>
          ),
        },
        {
          path: ":ledgerId",
          element: (
            <RequireAuth>
              <LedgerMonthlySummaryPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "profit&loss",
      element: (
        <RequireAuth>
          <ProfitLossPage />
        </RequireAuth>
      ),
    },
    {
      path: "memo-2-payment",
      element: (
        <RequireAuth>
          <Memo2PaymentPage />
        </RequireAuth>
      ),
    },
    {
      path: "employees",
      element: (
        <RequireAuth>
          <EmployeesPage />
        </RequireAuth>
      ),
    },
    {
      path: "employees/:employeeId",
      element: (
        <RequireAuth>
          <EmployeeDetailsPage />
        </RequireAuth>
      ),
    },
    {
      path: "outward-stock-registry",
      children: [
        {
          index: true,
          element: (
            <RequireAuth>
              <OutwardStockRegistryPage />
            </RequireAuth>
          ),
        },
        {
          path: "add",
          element: (
            <RequireAuth>
              <AddOutwardStockRegistryPage />
            </RequireAuth>
          ),
        },
        {
          path: ":id",
          element: (
            <RequireAuth>
              <OutwardStockRegistryDetailsPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "journals",
      children: [
        {
          path: "dashboard",
          element: (
            <RequireAuth>
              <JournalsPage />
            </RequireAuth>
          ),
        },
        {
          path: "add",
          element: (
            <RequireAuth>
              <AddJournalsPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "reports",
      children: [
        {
          path: "purchase",
          children: [
            {
              path: "party-wise",
              element: (
                <RequireAuth>
                  <PartyWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "item-wise",
              element: (
                <RequireAuth>
                  <ItemWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "batch-wise",
              element: (
                <RequireAuth>
                  <BatchWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "mfr-wise",
              element: (
                <RequireAuth>
                  <MfrWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "purchase-registry",
              element: (
                <RequireAuth>
                  <PurchaseRegistryReportPage />
                </RequireAuth>
              ),
            },
          ],
        },
        {
          path: "sales",
          children: [
            {
              path: "day-wise",
              element: (
                <RequireAuth>
                  <DayWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "customers",
              element: (
                <RequireAuth>
                  <CustomersReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "item-wise",
              element: (
                <RequireAuth>
                  <SalesItemWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "batch-wise",
              element: (
                <RequireAuth>
                  <SalesBatchWiseReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "sales-registry",
              element: (
                <RequireAuth>
                  <SalesRegistryReportPage />
                </RequireAuth>
              ),
            },
          ],
        },
        {
          path: "journals",
          children: [
            {
              path: "day-book",
              element: (
                <RequireAuth>
                  <DayBookReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "cash-book",
              element: (
                <RequireAuth>
                  <CashBookReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "cash-book/details",
              element: (
                <RequireAuth>
                  <CashBookDetailsPage />
                </RequireAuth>
              ),
            },
            {
              path: "trail-balance",
              element: (
                <RequireAuth>
                  <TrailBalanceReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "creditors",
              element: (
                <RequireAuth>
                  <CreditorsReportPage />
                </RequireAuth>
              ),
            },
            {
              path: "debitors",
              element: (
                <RequireAuth>
                  <DebitorsReportPage />
                </RequireAuth>
              ),
            },
          ],
        },
      ],
    },
    {
      path: "gst",
      children: [
        {
          path: "inward-suppliers",
          element: (
            <RequireAuth>
              <InwardSuppliersPage />
            </RequireAuth>
          ),
        },
        {
          path: "outward-suppliers",
          element: (
            <RequireAuth>
              <OutwardSuppliersPage />
            </RequireAuth>
          ),
        },
        {
          path: "company",
          element: (
            <RequireAuth>
              <CompanyGSTPage />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "offers",
      element: (
        <RequireAuth>
          <OffersPage />
        </RequireAuth>
      ),
    },
    {
      path: "/errors/:errorCode",
      element: <ErrorPage />,
    },
    {
      path: "*",
      element: <Navigate to="/errors/404" />,
    },
  ];

  const router = isElectron()
    ? createHashRouter(routerInitial)
    : createBrowserRouter(routerInitial);

  return (
    <>
      {hydrated && !authStore.isDetailsLoading ? (
        <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
          <Suspense fallback={<Backdrop />}>
            {isElectron() && <OnlineStatus />}
            <ModalsProvider>
              <RouterProvider router={router} />
            </ModalsProvider>
          </Suspense>
        </ErrorBoundary>
      ) : (
        <Backdrop />
      )}
    </>
  );
});

export default App;
